import React, { useState, useEffect } from 'react';
import {
    Box, Paper, IconButton, Typography, CircularProgress,
    TextField, Stack, Chip, alpha, useTheme, Divider,
    Grid, Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { getAllCashMemos, updateCashMemo } from '../../Service/cashmemo.service';
import { ICashMemoData, ICashMemoResponse } from '../../definitions/app/core';
import toast from 'react-hot-toast';

const StatBadge = ({ label, value, color }: { label: string; value: number; color: 'info' | 'success' | 'warning' | 'error' }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 70,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette[color].main, 0.08),
                border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
                textAlign: 'center',
            }}
        >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                {label}
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} color={`${color}.dark`}>
                {value}
            </Typography>
        </Box>
    );
};

const CashMemoUpdate = ({ isActive }: { isActive: boolean }) => {
    const theme = useTheme();
    const [cashMemos, setCashMemos] = useState<ICashMemoData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editedEntry, setEditedEntry] = useState<ICashMemoData | null>(null);

    const fetchCashMemos = async () => {
        setLoading(true);
        try {
            const result: ICashMemoResponse = await getAllCashMemos();
            if (result?.data.length > 0) {
                setCashMemos(result.data.slice(0, 5));
                toast.success(result.message || 'Cash memos fetched.');
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to fetch cash memos.');
            setCashMemos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isActive) fetchCashMemos();
    }, [isActive]);

    const handleEdit = (entry: ICashMemoData) => {
        setEditingId(entry._id || null);
        setEditedEntry({ ...entry });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditedEntry(null);
    };

    const handleChange = (field: keyof ICashMemoData, value: string | number) => {
        if (!editedEntry) return;
        const updated = { ...editedEntry, [field]: value };
        updated.totalCashMemoPunch = (updated.cdcmsCashMemoPunch || 0) + (updated.vitaranCashMemoPunch || 0);
        updated.remainingCashMemo = (updated.totalRefillDelivered || 0) - updated.totalCashMemoPunch;
        setEditedEntry(updated);
    };

    const handleSave = async () => {
        if (!editedEntry?._id) return;
        try {
            const result: ICashMemoResponse = await updateCashMemo(editedEntry._id, editedEntry);
            if (result.success) {
                toast.success('Cash Memo updated successfully!');
                setEditingId(null);
                setEditedEntry(null);
                fetchCashMemos();
            } else {
                toast.error(result.message || 'Update failed.');
            }
        } catch (error: any) {
            toast.error(error.message || 'An error occurred.');
        }
    };

    const getRemainingColor = (remaining: number): 'success' | 'error' | 'warning' => {
        if (remaining < 0) return 'error';
        if (remaining === 0) return 'success';
        return 'warning';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (cashMemos.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 5,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                }}
            >
                <Typography color="text.secondary">No recent cash memo records found.</Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    Recent {cashMemos.length} records
                </Typography>
                <Chip
                    label="Latest 5"
                    size="small"
                    sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        color: 'secondary.dark',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                    }}
                />
            </Stack>

            <Stack spacing={2}>
                {cashMemos.map((entry) => {
                    const isEditing = editingId === entry._id;
                    const data = isEditing ? editedEntry! : entry;
                    const remaining = data.remainingCashMemo || 0;

                    return (
                        <Paper
                            key={entry._id}
                            elevation={0}
                            sx={{
                                border: `1px solid ${isEditing ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
                                borderRadius: 2,
                                overflow: 'hidden',
                                transition: 'all 0.2s',
                                ...(isEditing && {
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                                }),
                                '&:hover': !isEditing ? {
                                    borderColor: alpha(theme.palette.primary.main, 0.25),
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.12)}`,
                                } : {},
                            }}
                        >
                            {/* Card header */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    bgcolor: isEditing
                                        ? alpha(theme.palette.primary.main, 0.06)
                                        : alpha(theme.palette.grey[500], 0.04),
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <PersonOutlineIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                        <Typography variant="caption" fontWeight={700} color="primary.main">
                                            {entry.agentName}
                                        </Typography>
                                    </Stack>
                                    <Divider orientation="vertical" flexItem />
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <CalendarTodayIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(entry.date || '').toLocaleDateString('en-IN', {
                                                day: '2-digit', month: 'short', year: 'numeric',
                                            })}
                                        </Typography>
                                    </Stack>
                                </Stack>

                                {/* Action buttons */}
                                {isEditing ? (
                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Save changes">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={handleSave}
                                                sx={{
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                                                }}
                                            >
                                                <SaveOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={handleCancelEdit}
                                                sx={{
                                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.15) },
                                                }}
                                            >
                                                <CancelOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                ) : (
                                    <Tooltip title="Edit this entry">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(entry)}
                                            sx={{
                                                color: 'primary.main',
                                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14) },
                                            }}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {/* Card body */}
                            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                {isEditing ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                CDCMS Punch
                                            </Typography>
                                            <TextField
                                                type="number"
                                                value={data.cdcmsCashMemoPunch}
                                                onChange={(e) => handleChange('cdcmsCashMemoPunch', Number(e.target.value))}
                                                fullWidth
                                                size="small"
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Vitaran Punch
                                            </Typography>
                                            <TextField
                                                type="number"
                                                value={data.vitaranCashMemoPunch}
                                                onChange={(e) => handleChange('vitaranCashMemoPunch', Number(e.target.value))}
                                                fullWidth
                                                size="small"
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Total Punch
                                            </Typography>
                                            <Box
                                                sx={{
                                                    height: 40,
                                                    px: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    bgcolor: alpha(theme.palette.info.main, 0.08),
                                                    borderRadius: 1,
                                                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={700} color="info.dark">
                                                    {data.totalCashMemoPunch}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Remaining
                                            </Typography>
                                            <Box
                                                sx={{
                                                    height: 40,
                                                    px: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    bgcolor: alpha(theme.palette[getRemainingColor(remaining)].main, 0.08),
                                                    borderRadius: 1,
                                                    border: `1px solid ${alpha(theme.palette[getRemainingColor(remaining)].main, 0.2)}`,
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={700} color={`${getRemainingColor(remaining)}.dark`}>
                                                    {remaining}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Remarks
                                            </Typography>
                                            <TextField
                                                value={data.remarks || ''}
                                                onChange={(e) => handleChange('remarks', e.target.value)}
                                                fullWidth
                                                size="small"
                                                placeholder="Add remarks..."
                                            />
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <StatBadge label="Refill" value={entry.totalRefillDelivered} color="info" />
                                            <StatBadge label="CDCMS" value={entry.cdcmsCashMemoPunch} color="info" />
                                            <StatBadge label="Vitaran" value={entry.vitaranCashMemoPunch} color="info" />
                                            <StatBadge label="Total Punch" value={entry.totalCashMemoPunch} color="warning" />
                                            <StatBadge label="Remaining" value={entry.remainingCashMemo} color={getRemainingColor(entry.remainingCashMemo)} />
                                        </Stack>
                                        {entry.remarks && (
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" color="text.secondary">Remarks: </Typography>
                                                <Typography variant="caption" color="text.primary">{entry.remarks}</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Paper>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default CashMemoUpdate;
